import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useState } from "react";
import type { GoalItemRecord } from "../../shared/domain";
import { descendantLeafCompletion } from "../../shared/scoring";
import type { CachedList } from "../lib/offline-db";
import type { ClientMutation } from "../lib/sync";

type Props = {
  list: CachedList;
  readOnly?: boolean;
  onMutate: (mutation: ClientMutation) => Promise<void>;
};

export function TenList({ list, readOnly = false, onMutate }: Props) {
  const roots = list.items.filter((item) => item.parentId === null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 7 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const reorder = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const moving = list.items.find((item) => item.id === active.id);
    const target = list.items.find((item) => item.id === over.id);
    if (!moving || !target || moving.parentId !== target.parentId) return;
    void onMutate({
      type: "reorder",
      payload: { itemId: moving.id, overItemId: target.id },
    });
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={reorder}
    >
      <SortableContext
        items={roots.map((root) => root.id)}
        strategy={verticalListSortingStrategy}
      >
        <ol className="ten-list" aria-label="Ten top-level Things">
          {Array.from({ length: 10 }, (_, index) => {
            const slot = index + 1;
            const root = roots.find((item) => item.rootSlot === slot);
            return (
              <li
                className={`slot ${root ? "slot--filled" : "slot--empty"}`}
                key={slot}
              >
                <span className="slot__number" aria-hidden="true">
                  {String(slot).padStart(2, "0")}
                </span>
                {root ? (
                  <ThingBranch
                    item={root}
                    list={list}
                    depth={0}
                    readOnly={readOnly}
                    onMutate={onMutate}
                  />
                ) : (
                  <EmptySlot
                    slot={slot}
                    disabled={readOnly}
                    onMutate={onMutate}
                  />
                )}
              </li>
            );
          })}
        </ol>
      </SortableContext>
    </DndContext>
  );
}

function EmptySlot({
  slot,
  disabled,
  onMutate,
}: {
  slot: number;
  disabled: boolean;
  onMutate: Props["onMutate"];
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState("");
  const submit = async () => {
    const value = title.trim();
    if (!value) {
      setEditing(false);
      return;
    }
    await onMutate({
      type: "add",
      payload: {
        itemId: crypto.randomUUID(),
        parentId: null,
        rootSlot: slot,
        siblingOrder: slot,
        title: value,
      },
    });
    setTitle("");
    setEditing(false);
  };
  if (!editing)
    return (
      <button
        className="slot__add"
        type="button"
        disabled={disabled}
        onClick={() => setEditing(true)}
      >
        Add a Thing
      </button>
    );
  return (
    <form
      className="inline-editor"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <label className="sr-only" htmlFor={`slot-${slot}`}>
        Thing for slot {slot}
      </label>
      <input
        id={`slot-${slot}`}
        autoFocus
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        maxLength={500}
        placeholder="What matters?"
        onKeyDown={(event) => {
          if (event.key === "Escape") setEditing(false);
        }}
      />
      <button type="submit" className="icon-button" aria-label="Save Thing">
        ↵
      </button>
    </form>
  );
}

function ThingBranch({
  item,
  list,
  depth,
  readOnly,
  onMutate,
}: {
  item: GoalItemRecord;
  list: CachedList;
  depth: number;
  readOnly: boolean;
  onMutate: Props["onMutate"];
}) {
  const children = list.items
    .filter((candidate) => candidate.parentId === item.id)
    .sort((a, b) => a.siblingOrder - b.siblingOrder);
  const [title, setTitle] = useState(item.title);
  const [adding, setAdding] = useState(false);
  const [childTitle, setChildTitle] = useState("");
  const complete =
    children.length > 0
      ? descendantLeafCompletion(item.id, list.items)
      : item.completed;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: readOnly });

  const rename = async () => {
    const value = title.trim();
    if (!value) {
      setTitle(item.title);
      return;
    }
    if (value !== item.title)
      await onMutate({
        type: "rename",
        payload: { itemId: item.id, title: value },
      });
  };
  const addChild = async () => {
    const value = childTitle.trim();
    if (!value) return;
    await onMutate({
      type: "add",
      payload: {
        itemId: crypto.randomUUID(),
        parentId: item.id,
        rootSlot: null,
        siblingOrder: children.length,
        title: value,
      },
    });
    setChildTitle("");
    setAdding(false);
  };
  const outdent = async () => {
    if (!item.parentId) return;
    const parent = list.items.find(
      (candidate) => candidate.id === item.parentId,
    );
    if (!parent) return;
    if (parent.parentId) {
      await onMutate({
        type: "move",
        payload: {
          itemId: item.id,
          parentId: parent.parentId,
          rootSlot: null,
          siblingOrder: parent.siblingOrder + 1,
        },
      });
    } else {
      const filled = new Set(
        list.items
          .filter((candidate) => candidate.parentId === null)
          .map((candidate) => candidate.rootSlot),
      );
      const slot = Array.from({ length: 10 }, (_, index) => index + 1).find(
        (candidate) => !filled.has(candidate),
      );
      if (slot)
        await onMutate({
          type: "move",
          payload: {
            itemId: item.id,
            parentId: null,
            rootSlot: slot,
            siblingOrder: slot,
          },
        });
    }
  };
  const indent = async () => {
    const siblings = list.items
      .filter((candidate) => candidate.parentId === item.parentId)
      .sort((a, b) => a.siblingOrder - b.siblingOrder);
    const index = siblings.findIndex((candidate) => candidate.id === item.id);
    const previous = siblings[index - 1];
    if (previous)
      await onMutate({
        type: "move",
        payload: {
          itemId: item.id,
          parentId: previous.id,
          rootSlot: null,
          siblingOrder: list.items.filter(
            (candidate) => candidate.parentId === previous.id,
          ).length,
        },
      });
  };

  return (
    <div
      ref={setNodeRef}
      className={`thing-wrap ${isDragging ? "thing-wrap--dragging" : ""}`}
      style={
        {
          "--depth": Math.min(depth, 8),
          transform: CSS.Transform.toString(transform),
          transition,
        } as React.CSSProperties
      }
    >
      <div className={`thing ${complete ? "thing--complete" : ""}`}>
        <button
          type="button"
          className="thing__check"
          disabled={readOnly}
          aria-label={`${complete ? "Reopen" : "Complete"} ${item.title}`}
          aria-pressed={complete}
          onClick={() => {
            const completeSubtree =
              children.length > 0 && !complete
                ? window.confirm("Complete this entire subtree?")
                : false;
            void onMutate({
              type: "complete",
              payload: {
                itemId: item.id,
                completed: !complete,
                completeSubtree,
              },
            });
          }}
        >
          {complete ? "✓" : ""}
        </button>
        <input
          className="thing__title"
          aria-label={`Thing: ${item.title}`}
          value={title}
          disabled={readOnly}
          maxLength={500}
          onChange={(event) => setTitle(event.target.value)}
          onBlur={() => void rename()}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
            if (event.altKey && event.key === "ArrowLeft") {
              event.preventDefault();
              void outdent();
            }
            if (event.altKey && event.key === "ArrowRight") {
              event.preventDefault();
              void indent();
            }
          }}
        />
        {!readOnly && (
          <div className="thing__actions">
            <button
              type="button"
              className="thing__drag"
              {...attributes}
              {...listeners}
              aria-label={`Reorder ${item.title}. Use Space, then arrow keys, then Space to drop.`}
            >
              ⠿
            </button>
            <button
              type="button"
              onClick={() => setAdding(true)}
              aria-label={`Add a nested Thing under ${item.title}`}
            >
              ＋
            </button>
            <button
              type="button"
              onClick={() => void outdent()}
              disabled={!item.parentId}
              aria-label="Outdent"
            >
              ←
            </button>
            <button
              type="button"
              onClick={() => void indent()}
              aria-label="Indent"
            >
              →
            </button>
            {children.length > 0 && (
              <button
                type="button"
                onClick={() =>
                  void onMutate({
                    type: "collapse",
                    payload: { itemId: item.id, collapsed: !item.collapsed },
                  })
                }
                aria-label={
                  item.collapsed
                    ? "Expand nested Things"
                    : "Collapse nested Things"
                }
              >
                {item.collapsed ? "▸" : "▾"}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (
                  window.confirm(
                    `Delete “${item.title}” and its nested Things?`,
                  )
                )
                  void onMutate({
                    type: "delete",
                    payload: { itemId: item.id },
                  });
              }}
              aria-label={`Delete ${item.title}`}
            >
              ×
            </button>
          </div>
        )}
      </div>
      {adding && (
        <form
          className="nested-editor"
          onSubmit={(event) => {
            event.preventDefault();
            void addChild();
          }}
        >
          <input
            autoFocus
            value={childTitle}
            maxLength={500}
            placeholder="Add a nested Thing…"
            aria-label={`Nested Thing under ${item.title}`}
            onChange={(event) => setChildTitle(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Escape") setAdding(false);
            }}
          />
          <button type="submit">Add</button>
        </form>
      )}
      {!item.collapsed && children.length > 0 && (
        <SortableContext
          items={children.map((child) => child.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="thing__children">
            {children.map((child) => (
              <ThingBranch
                key={child.id}
                item={child}
                list={list}
                depth={depth + 1}
                readOnly={readOnly}
                onMutate={onMutate}
              />
            ))}
          </div>
        </SortableContext>
      )}
    </div>
  );
}
